import { useEffect, useState } from "react";
import { callFunction, supabase } from "../lib/supabase.js";
import { CouplePortal, fmt } from "../dashboard.jsx";
import { Loading, Message, ErrorText } from "./common.jsx";
import { StatusTag, AgreementDoc } from "../ui.jsx";

export function PublicPortal({ token }) {
  const [data, setData] = useState(undefined);
  const [err, setErr] = useState("");
  const [section, setSection] = useState(null);
  const [peopleNote, setPeopleNote] = useState({});

  const load = () => callFunction("portal", { action: "get", token })
    .then(d => { setData(d); setSection(s => s || ((d.quote && d.quote.canRespond) || (d.contract && d.contract.canRespond) ? "approvals" : "profile")); })
    .catch(e => { setErr(e.message); setData(null); });
  useEffect(() => { load(); }, [token]);

  if (data === undefined) return <Loading />;
  if (!data) return <Message title="This portal link isn't working" body={err || "It may have been turned off. Ask your planner for a new link."} />;

  const hasDocs = !!(data.quote || data.contract);
  const waiting = (data.quote && data.quote.canRespond) || (data.contract && data.contract.canRespond);
  const extraSections = hasDocs ? [{ key: "approvals", label: "Proposal & agreement", first: true, badge: waiting, render: () => <Approvals data={data} token={token} onDone={load} /> }] : [];
  // Phase 11 · Step 7: what the agreement asks of the couple, with dates. Phase 12: and where they send me files.
  extraSections.push({ key: "needs", label: "What I need from you", render: () => <Needs needs={data.needs || []} uploads={data.uploads || []} token={token} onDone={load} /> });

  // Batch C · Step 12: the couple keeps their own wedding-party list; each change saves the whole list.
  const savePeople = async list => {
    setData(d => ({ ...d, people: list }));
    setPeopleNote({ busy: true });
    try { const r = await callFunction("portal", { action: "people-save", token, people: list }); setData(d => ({ ...d, people: r.people })); setPeopleNote({ saved: true }); }
    catch (e) { setPeopleNote({ err: e.message }); }
  };
  const note = peopleNote.err ? <ErrorText>{peopleNote.err}</ErrorText> : peopleNote.busy ? <div className="field-hint" role="status">Saving…</div> : peopleNote.saved ? <div className="field-hint" role="status">Saved. I can see it now.</div> : null;
  return <CouplePortal client={data.client} vendors={data.vendors} milestones={data.milestones} dayOf={data.dayOf} expenses={data.expenses}
    fees={data.fees || []} budgets={data.budgets || null}
    people={data.people || []} onPeople={savePeople} peopleNote={note}
    portalSection={section || "profile"} setPortalSection={setSection} extraSections={extraSections} studioName={data.studioName} />;
}

function Needs({ needs, uploads, token, onDone }) {
  const open = needs.filter(n => !n.done).length;
  return (
    <div className="stack" style={{ "--gap": "22px" }}>
    <section className="panel measure deck-card" style={{ "--measure": "780px" }} aria-label="What I need from you">
      <div className="label">{needs.length ? "From our agreement" : "Your planning portal"}</div>
      <h2 className="panel-title" style={{ fontSize: 34, marginTop: 6 }}>What I need from you</h2>
      <p className="text-muted" style={{ margin: 0, fontSize: 16.5, lineHeight: 1.55 }}>{!needs.length ? "Nothing yet. If you have something for me, such as a vendor contract or the guest list, send it below." : open ? "A few things only you can send me, and when I need them." : "All done. Thank you."}</p>
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {needs.map((n, i) => (
          <li key={i} className="list-row" style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "nowrap" }}>
            <div style={{ flex: "1 1 auto", minWidth: 0 }}>
              <div style={{ fontSize: 17.5, fontWeight: 500 }}>{n.title}</div>
              <div className="text-muted" style={{ fontSize: 15.5, lineHeight: 1.5 }}>{n.description}</div>
            </div>
            <div style={{ flex: "0 0 auto", textAlign: "right" }}>
              {n.done ? <StatusTag status="Done" /> : <><div className="label">Due</div><div className="figure">{n.due}</div></>}
            </div>
          </li>
        ))}
      </ul>
    </section>
    <SendFile needs={needs.filter(n => !n.done)} uploads={uploads} token={token} onDone={onDone} />
    </div>
  );
}

// Phase 12 · Step 6: send me a file. PDFs and photos up to 20 MB, ten a day; it goes straight into my Documents.
function SendFile({ needs, uploads, token, onDone }) {
  const [file, setFile] = useState(null);
  const [forTask, setForTask] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState("");
  const [inputKey, setInputKey] = useState(0);
  const send = async () => {
    if (!file) return;
    setBusy(true); setErr(""); setDone("");
    try {
      if (file.size > 20 * 1024 * 1024) throw new Error("That file is over 20 MB. Try a smaller copy, or email it to me instead.");
      const start = await callFunction("portal", { action: "upload-start", token, name: file.name, type: file.type, size: file.size });
      const { error } = await supabase.storage.from("documents").uploadToSignedUrl(start.path, start.uploadToken, file, { contentType: start.type });
      if (error) throw new Error("The upload didn't finish. Check your connection and try again.");
      await callFunction("portal", { action: "upload-done", token, path: start.path, name: file.name, type: start.type, note, forTask });
      setDone("Got it. “" + file.name + "” is with me now. Thank you.");
      setFile(null); setNote(""); setForTask(""); setInputKey(k => k + 1);
      await onDone();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <section className="panel measure deck-card" style={{ "--measure": "780px" }} aria-labelledby="send-file-h">
      <div className="label">Files</div>
      <h2 id="send-file-h" className="panel-title" style={{ fontSize: 30, marginTop: 6 }}>Send me a file</h2>
      <p className="text-muted" style={{ margin: 0, fontSize: 16, lineHeight: 1.55 }}>PDFs and photos (JPG, PNG or HEIC), up to 20 MB each. They go straight to me and nowhere else.</p>
      <label className="stack" style={{ "--gap": "8px" }}>
        <span className="field-label">Choose a file</span>
        <input key={inputKey} className="input" type="file" accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,application/pdf,image/jpeg,image/png,image/heic" onChange={e => { setFile(e.target.files && e.target.files[0] || null); setDone(""); setErr(""); }} />
      </label>
      {needs.length > 0 && (
        <label className="stack" style={{ "--gap": "8px" }}>
          <span className="field-label">What is it for?</span>
          <select className="input" value={forTask} onChange={e => setForTask(e.target.value)}>
            <option value="">Something else</option>
            {needs.map(n => <option key={n.id} value={n.id}>{n.title}</option>)}
          </select>
        </label>
      )}
      <label className="stack" style={{ "--gap": "8px" }}>
        <span className="field-label">A note for me (optional)</span>
        <input className="input" value={note} onChange={e => setNote(e.target.value)} />
      </label>
      <div><button className="btn btn-accent" disabled={!file || busy} onClick={send}>{busy ? "Sending…" : "Send file"}</button></div>
      {done && <div role="status" className="note note-ok">{done}</div>}
      <ErrorText>{err}</ErrorText>
      {uploads.length > 0 && (
        <div>
          <div className="label" style={{ marginBottom: 4 }}>What you&rsquo;ve sent</div>
          {uploads.slice().reverse().map((u, i) => (
            <div key={i} className="list-row list-split" style={{ fontSize: 15.5 }}>
              <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{u.name}{u.forTaskTitle ? <span className="text-muted"> · {u.forTaskTitle}</span> : null}</span>
              <span className="figure text-muted" style={{ fontSize: 13.5 }}>{u.receivedAt ? new Date(u.receivedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// "The proposal" and "The agreement", set like the brand deck's pricing card:
// Courier label, Cormorant title, Courier price, one oxblood action.
function Card({ label, title, tag, children }) {
  return (
    <section className="panel measure deck-card" style={{ "--measure": "780px" }} aria-label={title}>
      <div className="panel-head" style={{ alignItems: "flex-start" }}>
        <div style={{ flex: "1 1 220px", minWidth: 0 }}>
          <div className="label">{label}</div>
          <h2 className="panel-title" style={{ fontSize: 34, marginTop: 6 }}>{title}</h2>
        </div>
        {tag && <StatusTag status={tag} />}
      </div>
      {children}
    </section>
  );
}

function Approvals({ data, token, onDone }) {
  // Oxblood once per view: only the first thing waiting on the couple gets the oxblood button.
  const quoteFirst = !!(data.quote && data.quote.canRespond);
  return (
    <div className="stack" style={{ "--gap": "22px" }}>
      {data.quote && <QuoteCard quote={data.quote} token={token} onDone={onDone} studioName={data.studioName} primary={quoteFirst} />}
      {data.contract && <ContractCard contract={data.contract} partners={(data.client && data.client.partners) || []} token={token} onDone={onDone} primary={!quoteFirst} />}
    </div>
  );
}

function useRespond(token, kind, onDone) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState("");
  const respond = async (action, extra, doneMsg) => {
    setBusy(true); setErr("");
    try { await callFunction("portal", { action: "respond", token, kind, response: action, ...extra }); setDone(doneMsg); await onDone(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return { busy, err, done, respond };
}

function ChangesBox({ busy, onSend, onCancel }) {
  const [text, setText] = useState("");
  return (
    <div className="stack" style={{ "--gap": "12px", borderTop: "1px solid var(--rule)", paddingTop: 16 }}>
      <label className="stack" style={{ "--gap": "8px" }}>
        <span className="field-label">What would you like changed?</span>
        <textarea className="input" style={{ minHeight: 110 }} value={text} onChange={e => setText(e.target.value)}></textarea>
      </label>
      <div className="row" style={{ "--gap": "10px" }}>
        <button className="btn btn-primary" disabled={busy || !text.trim()} onClick={() => onSend(text)}>{busy ? "Sending…" : "Send my note"}</button>
        <button className="btn btn-secondary" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function QuoteCard({ quote, token, onDone, studioName, primary }) {
  const { busy, err, done, respond } = useRespond(token, "quote", onDone);
  const [asking, setAsking] = useState(false);
  const investment = quote.finalNumber ? fmt(quote.finalNumber) : fmt(quote.rangeLow) + " – " + fmt(quote.rangeHigh);
  const tag = quote.status === "Client approved" ? "Approved" : quote.status === "Client declined" ? "Declined" : quote.canRespond ? "Awaiting your review" : null;
  return (
    <Card label="Your planning proposal" title={quote.packageTier || "The proposal"} tag={tag}>
      <div>
        <div className="label">Planning investment</div>
        <div className="deck-price">{investment}</div>
      </div>
      {quote.lineItems.length > 0 && (
        <div>
          {quote.lineItems.map((li, i) => (
            <div key={i} className="list-row list-split" style={{ fontSize: 16 }}>
              <span>{li.label}{li.note ? <span className="text-muted" style={{ display: "block", fontSize: 15 }}>{li.note}</span> : null}</span>
              <span className="figure">{fmt(li.amount)}</span>
            </div>
          ))}
        </div>
      )}
      {done && <div role="status" className="note note-ok">{done}</div>}
      {quote.canRespond && !asking && (
        <div className="row" style={{ "--gap": "10px", borderTop: "1px solid var(--rule)", paddingTop: 16 }}>
          <button className={"btn " + (primary ? "btn-accent" : "btn-primary")} disabled={busy} onClick={() => { if (window.confirm("Approve this proposal? I'll prepare your agreement next.")) respond("approve", {}, "Thank you. I'll prepare your agreement and send it next."); }}>{busy ? "Saving…" : "Approve proposal"}</button>
          <button className="btn btn-secondary" disabled={busy} onClick={() => setAsking(true)}>Request changes</button>
        </div>
      )}
      {quote.canRespond && asking && <ChangesBox busy={busy} onCancel={() => setAsking(false)} onSend={t => respond("changes", { comment: t }, "Sent. I'll update the proposal and get back to you.").then(() => setAsking(false))} />}
      <ErrorText>{err}</ErrorText>
    </Card>
  );
}

const when = iso => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
const longDay = ymd => { const [y, m, d] = String(ymd).split("-").map(Number); return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", { timeZone: "UTC", month: "long", day: "numeric", year: "numeric" }); };

// Phase 11 · Step 4: each partner signs for themselves, then I countersign.
function ContractCard({ contract, partners, token, onDone, primary }) {
  const { busy, err, done, respond } = useRespond(token, "contract", onDone);
  const [asking, setAsking] = useState(false);
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const sigs = contract.signatures || [];
  // Phase 12 · Batch C: the agreement's own signers (one or two; a parent can sign). Older agreements: the partners.
  const names = Array.isArray(contract.signers) && contract.signers.length ? contract.signers.map(x => x.name)
    : [0, 1].map(i => (partners[i] && partners[i].name) || (i ? "Partner 2" : "Partner 1"));
  const idx = names.map((_, i) => i);
  const solo = names.length === 1;
  const unsigned = idx.filter(i => !sigs.some(x => x.partner === i));
  const [who, setWho] = useState(null);
  const signer = unsigned.includes(who) ? who : (unsigned.length === 1 ? unsigned[0] : null);
  const executed = contract.status === "Executed";
  const legacy = contract.status === "Signed";
  const tag = executed ? "Executed" : legacy ? "Signed" : contract.status === "Signed by couple" ? (solo ? "Signed" : "Signed by both of you") : contract.canRespond ? (sigs.length ? "Waiting on " + names[unsigned[0]] : "Ready to sign") : null;
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfErr, setPdfErr] = useState("");
  const download = async () => {
    setPdfBusy(true); setPdfErr("");
    try { const r = await callFunction("portal", { action: "signed-pdf", token }); window.location.assign(r.url); }
    catch (e) { setPdfErr(e.message); } finally { setPdfBusy(false); }
  };
  return (
    <Card label="Your coordination agreement" title="The agreement" tag={tag}>
      {/* The agreement reads like a document on white paper. */}
      <div className="agreement-paper" tabIndex={0} role="region" aria-label="Agreement text"><AgreementDoc text={contract.body} /></div>

      {(sigs.length > 0 || contract.countersignature || (legacy && contract.signature)) && (
        <div className="signature-block" style={{ alignItems: "flex-start" }}>
          <img src="/brand/wax-seal.svg" alt="" width="44" height="44" />
          <ul className="stack" style={{ "--gap": "8px", listStyle: "none", margin: 0, padding: 0 }} aria-label="Signatures">
            {legacy && contract.signature && <li><div className="label" style={{ color: "var(--ink)" }}>Signed by {contract.signature.name}</div><div className="label">{when(contract.signature.at)}</div></li>}
            {!legacy && idx.map(i => {
              const sg = sigs.find(x => x.partner === i);
              return (
                <li key={i}>
                  <div className="label" style={{ color: sg ? "var(--ink)" : undefined }}>{sg ? "Signed by " + sg.name : "Waiting on " + names[i]}</div>
                  {sg && <div className="label">{when(sg.at)}</div>}
                </li>
              );
            })}
            {contract.countersignature && <li><div className="label" style={{ color: "var(--ink)" }}>Countersigned by {contract.countersignature.name}, {contract.countersignature.title}</div><div className="label">{when(contract.countersignature.at)}</div></li>}
            {!legacy && !executed && sigs.length >= names.length && <li><div className="label">Waiting on my countersignature</div></li>}
          </ul>
        </div>
      )}
      {executed && (
        <div className="stack" style={{ "--gap": "10px", borderTop: "1px solid var(--rule)", paddingTop: 16 }}>
          <p style={{ margin: 0, fontSize: 16.5, lineHeight: 1.55 }}>Our agreement is in effect as of {longDay(contract.effectiveDate)}.</p>
          {contract.hasPdf && <div><button className={"btn " + (primary ? "btn-accent" : "btn-primary")} disabled={pdfBusy} onClick={download}>{pdfBusy ? "Opening…" : "Download the signed agreement"}</button></div>}
          <ErrorText>{pdfErr}</ErrorText>
        </div>
      )}

      {done && <div role="status" className="note note-ok">{done}</div>}
      {contract.canRespond && !asking && signer === null && (
        <div className="stack" style={{ "--gap": "12px", borderTop: "1px solid var(--rule)", paddingTop: 16 }} role="group" aria-labelledby="who-signs">
          <div className="field-label" id="who-signs">Who's signing?</div>
          <div className="row" style={{ "--gap": "10px" }}>
            {unsigned.map(i => (
              <button key={i} className={"btn " + (primary && i === unsigned[0] ? "btn-accent" : "btn-primary")} onClick={() => { setWho(i); setName(""); setAgree(false); }}>I'm {names[i]}</button>
            ))}
            {contract.canRequestChanges && <button className="btn btn-secondary" disabled={busy} onClick={() => setAsking(true)}>Request changes</button>}
          </div>
          <div className="field-hint">{solo ? names[0] + " signs for the client side of the agreement." : "Each of you signs for yourself. You can do it on the same device, one after the other."}</div>
        </div>
      )}
      {contract.canRespond && !asking && signer !== null && (
        <div className="stack" style={{ "--gap": "14px", borderTop: "1px solid var(--rule)", paddingTop: 16 }}>
          <div className="row" style={{ "--gap": "8px 14px", alignItems: "baseline" }}>
            <div className="label" style={{ color: "var(--ink)" }}>Signing as {names[signer]}</div>
            {unsigned.length > 1 && <button className="btn-link" onClick={() => { setWho(null); setName(""); setAgree(false); }}>Not {names[signer].split(" ")[0]}?</button>}
          </div>
          <label className="stack" style={{ "--gap": "8px", maxWidth: 420 }}>
            <span className="field-label">Type your full legal name to sign</span>
            <input className="input signature-input" value={name} onChange={e => setName(e.target.value)} autoComplete="name" />
          </label>
          <label style={{ display: "flex", gap: 12, alignItems: "flex-start", fontSize: 16, lineHeight: 1.5, cursor: "pointer" }}>
            <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} style={{ marginTop: 3 }} />
            <span>I have read this agreement and agree that typing my name above is my electronic signature.</span>
          </label>
          <div className="row" style={{ "--gap": "10px" }}>
            <button className={"btn " + (primary ? "btn-accent" : "btn-primary")} disabled={busy || !agree || name.trim().length < 3}
              onClick={() => respond("approve", { signedName: name.trim(), partner: signer },
                unsigned.length > 1 ? "Signed. Thank you. Once " + names[unsigned.find(i => i !== signer)].split(" ")[0] + " signs, I'll countersign." : solo ? "Signed. Thank you; I'll countersign next." : "Signed by both of you. Thank you; I'll countersign next.")
                .then(() => { setWho(null); setName(""); setAgree(false); })}>{busy ? "Signing…" : "Sign agreement"}</button>
            {contract.canRequestChanges && <button className="btn btn-secondary" disabled={busy} onClick={() => setAsking(true)}>Request changes</button>}
          </div>
        </div>
      )}
      {contract.canRespond && !contract.canRequestChanges && !executed && (
        <p className="text-muted" style={{ margin: 0, fontSize: 15 }}>One of you has signed, so changes go by email now.</p>
      )}
      {contract.canRequestChanges && asking && <ChangesBox busy={busy} onCancel={() => setAsking(false)} onSend={t => respond("changes", { comment: t }, "Sent. I'll revise the agreement and send it to you again.").then(() => setAsking(false))} />}
      <ErrorText>{err}</ErrorText>
    </Card>
  );
}
